<!-- eslint-disable no-irregular-whitespace -->
<template>
  <div class="space-y-32">
    <div class="space-y-24">
      <header class="space-y-8">
        <RevIllustration
          alt=""
          :height="30"
          src="/img/bouygues/bouyguesLogoPhone.svg"
          :width="70"
        />

        <FormattedMessage
          class="heading-1 whitespace-pre-line"
          :definition="translations.title"
          tag="h2"
        >
          <template #price>
            <span class="text-static-brand-hi">
              {{ i18n.price(highestSubsidyOffer.subsidy) }}
            </span>
            <template
              v-if="
                highestSubsidyOffer.baseOffer &&
                hasSubsidyBoost(highestSubsidyOffer)
              "
            >
               <span class="line-through">
                {{ i18n.price(highestSubsidyOffer.baseOffer.subsidy) }}
              </span>
            </template>
          </template>
        </FormattedMessage>
      </header>

      <div class="space-y-24">
        <OfferCard v-for="offer in offers" :key="offer.id" :offer />
      </div>

      <div
        v-if="benefits.includes('BBOX_DISCOUNT_5_EUROS')"
        class="flex items-center"
      >
        <div class="text-static-default-low caption mr-32">
          {{ i18n(translations.bboxOffer) }}
        </div>

        <RevTooltip content-class-names="z-20" position="left">
          <template #trigger="{ show, hide }">
            <IconInfo
              @focusin="show"
              @focusout="hide"
              @mouseenter="show"
              @mouseleave="hide"
            />
          </template>
          {{ i18n(translations.bboxOfferTooltip) }}
        </RevTooltip>
      </div>
    </div>

    <RevInfoBlock
      :content="i18n(translations.keepYourPhoneNumberContent)"
      :icon="IconContact"
      :title="i18n(translations.keepYourPhoneNumberTitle)"
      variant="default"
    />

    <p class="body-2">
      {{ i18n(translations.conditions) }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconContact } from '@ds/icons/IconContact'
import { IconInfo } from '@ds/icons/IconInfo'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep'

import getbenefitsFromMobilePlanOffers from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/getBenefitsFromMobilePlanOffers/getBenefitsFromMobilePlanOffers'

import translations from './BouyguesOffers.translations'
import OfferCard from './components/OfferCard/OfferCard.vue'

const i18n = useI18n()

type Offer = ReadonlyObjectDeep<MobilePlanOffer>

const props = defineProps<{
  offers: readonly Offer[]
}>()

const benefits = computed(() => getbenefitsFromMobilePlanOffers(props.offers))

function getHighestSubsidyOffer(offers: readonly Offer[]) {
  return offers.reduce<Offer>((highestSubsidyOffer, offer) => {
    const highestAmount = parseInt(highestSubsidyOffer.subsidy.amount, 10)
    const amount = parseInt(offer.subsidy.amount, 10)

    if (amount > highestAmount) return offer

    return highestSubsidyOffer
  }, offers[0])
}

function hasSubsidyBoost(offer: Offer) {
  return offer.baseOffer
    ? parseInt(offer.subsidy.amount, 10) >
        parseInt(offer.baseOffer.subsidy.amount, 10)
    : false
}

const highestSubsidyOffer = computed(() => {
  return getHighestSubsidyOffer(props.offers)
})
</script>
